import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Grid } from "../components/Cards";
import { Page } from "../components/Layout";
import { search } from "../lib/catalog";
import { useCatalog } from "../state/CatalogContext";
import type { Category } from "../types";

const LABELS: Record<Category, { title: string; empty: string }> = {
  movie: { title: "Películas", empty: "Aún no hay películas publicadas." },
  series: { title: "Series", empty: "Aún no hay series publicadas." },
  anime: { title: "Anime", empty: "Aún no hay anime publicado." },
};

export function BrowsePage({ category }: { category: Category }) {
  const { catalog } = useCatalog();
  const [genre, setGenre] = useState<string | null>(null);
  const [sort, setSort] = useState<"recent" | "az" | "year">("recent");
  const items = useMemo(() => {
    let list = catalog!.items.filter((t) => t.category === category);
    if (genre) list = list.filter((t) => t.genres?.includes(genre));
    return [...list].sort((a, b) =>
      sort === "az" ? a.title.localeCompare(b.title, "es") : sort === "year" ? (b.year ?? 0) - (a.year ?? 0) : b.addedAt.localeCompare(a.addedAt));
  }, [catalog, category, genre, sort]);
  const genres = [...new Set(catalog!.items.filter((t) => t.category === category).flatMap((t) => t.genres ?? []))].sort();
  return (
    <Page title={LABELS[category].title}>
      <div className="flex flex-wrap items-end gap-4">
        <h1 className="font-display text-4xl sm:text-5xl">{LABELS[category].title}</h1>
        <label className="ml-auto text-sm text-muted">
          Ordenar por{" "}
          <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} className="ml-1 rounded-md border border-line bg-surface px-2 py-1 text-text">
            <option value="recent">Añadidos recientemente</option>
            <option value="az">Título</option>
            <option value="year">Año</option>
          </select>
        </label>
      </div>
      {genres.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Filtrar por género">
          <Chip active={!genre} onClick={() => setGenre(null)}>Todos</Chip>
          {genres.map((g) => <Chip key={g} active={genre === g} onClick={() => setGenre(g)}>{g}</Chip>)}
        </div>
      )}
      <div className="mt-8 pb-16">{items.length ? <Grid items={items} /> : <p className="text-muted">{LABELS[category].empty}</p>}</div>
    </Page>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button onClick={onClick} aria-pressed={active} className={`rounded-full border px-3.5 py-1.5 text-sm ${active ? "border-accent/60 bg-accent/10 text-text" : "border-line text-muted hover:text-text"}`}>
      {children}
    </button>
  );
}

export function SearchPage() {
  const { catalog } = useCatalog();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  const results = useMemo(() => search(catalog!, q), [catalog, q]);
  return (
    <Page title="Buscar">
      <label htmlFor="q" className="sr-only">Buscar en Radiant</label>
      <input
        id="q" ref={ref} type="search" value={q} placeholder="Títulos, géneros, episodios…" autoComplete="off"
        onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {}, { replace: true })}
        className="w-full border-b border-line bg-transparent pb-3 font-display text-3xl outline-none placeholder:text-muted/60 focus:border-accent sm:text-5xl"
        data-testid="search-input"
      />
      <div className="mt-8 pb-16" aria-live="polite">
        {q && !results.length && <p className="text-muted">No hay resultados para «{q}». Prueba con otro título o un género.</p>}
        {results.length > 0 && <Grid items={results} />}
      </div>
    </Page>
  );
}
